"use client";

import { useState, useEffect } from "react";
import { createClient } from "@/lib/supabase/client";
import { User } from "@supabase/supabase-js";
import { LogOut, User as UserIcon, Shield } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLocale } from "@/i18n/context";
import { Skeleton } from "@/components/ui/Skeleton";
export function AuthUI() {
  const { t } = useLocale();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const router = useRouter();

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      setLoading(false);
    };

    fetchUser();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [supabase]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.push("/");
    router.refresh();
  };

  if (loading) return <Skeleton className="w-8 h-8 rounded-full" />;

  return (
    <div className="relative">
      {user ? (
        <div className="flex items-center gap-3">
          <Link 
            href="/admin" 
            className="flex items-center gap-2 text-sm font-extrabold bg-[#206A4F] text-[#F5F0E8] dark:bg-emerald-500 dark:text-zinc-950 px-4 md:px-5 py-2 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-200 cursor-pointer"
          >
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline md:inline">{t.nav.admin}</span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm font-extrabold px-3 py-2 rounded-full text-foreground/80 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-500/10 transition-all duration-200 active:scale-95 cursor-pointer"
            title={t.nav.logout}
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline md:inline">{t.nav.logout}</span>
          </button>
        </div>
      ) : (
        <Link
          href="/login"
          className="flex items-center gap-2 text-sm font-extrabold bg-[#206A4F] text-[#F5F0E8] dark:bg-emerald-500 dark:text-zinc-950 px-6 py-2.5 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-200 cursor-pointer"
        >
          <UserIcon className="w-4 h-4" />
          <span>{t.nav.login}</span>
        </Link>
      )}
    </div>
  );
}
